import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DraggableIcon } from '../../../assets/svgs/icons';
import ContextMenuIconButton from '../../ContextMenuIconButton';

interface PageNameCellValue {
  id: number;
  label: string;
  route?: string;
  draggable: boolean;
  isHidden: boolean;
  statusUnknown?: boolean;
  unavailableNote?: string;
  alwaysVisible?: boolean;
  onHideShow: Function;
}

/**
 * PAGE column cell for the Pages table. Mirrors the menu-item NameCell pattern
 * (drag handle + ContextMenuIconButton) but without an image.
 * - Edit  -> navigates to the page's editor route (About, Press, Gift Cards, and Locations for multi-location brands).
 * - Show/Hide -> bubbles up to PagesPage. Suppressed for always-visible rows (Menu, Contact) and
 *   for rows that can't be shown yet (`unavailableNote`, e.g. Gift Cards without a link).
 * When a row has neither action (Menu, Contact) the context menu button is omitted entirely.
 */
const PageNameCell = ({ value }: { value: PageNameCellValue }) => {
  const navigate = useNavigate();
  const { id, label, route, draggable, isHidden, statusUnknown, unavailableNote, alwaysVisible, onHideShow } = value;

  const onEditClicked = route ? () => navigate(`/${route}`) : null;
  const onHideShowClicked = alwaysVisible || unavailableNote ? null : onHideShow;
  const hasContextMenu = Boolean(onEditClicked || onHideShowClicked);

  return (
    <span className={draggable ? 'draggable-name-cell-container' : 'non-draggable-name-cell-container'}>
      {draggable && <DraggableIcon />}
      {hasContextMenu ? (
        <ContextMenuIconButton
          itemID={id}
          isHidden={isHidden}
          onEditClicked={onEditClicked}
          onHideShowClicked={onHideShowClicked}
        />
      ) : (
        // Menu/Contact have no actions — keep an empty slot so their labels align with the others.
        <span className="page-action-placeholder" aria-hidden />
      )}
      <div className={`name-container ${isHidden ? 'hidden-cell-value' : ''}`}>
        <span>{label}</span>
        {statusUnknown && (
          <span
            className="page-status-unknown"
            title="Couldn't load this page's current visibility — the status shown may be out of date."
          >
            status unavailable
          </span>
        )}
      </div>
      {unavailableNote && <span className="page-unavailable">{unavailableNote}</span>}
    </span>
  );
};

export default PageNameCell;
